import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFolderStore } from '@/store/folderStore';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Mail,
  Star,
  Send,
  FileText,
  Trash2,
  AlertCircle,
  Folder,
  RotateCw,
} from 'lucide-react';
import AddFolderModal from '@/components/email/AddFolderModal';
import { usePagination } from '@/contexts/PaginationContext';
import { formatBadgeCount } from '@/components/ui/CountBadge';
import { syncFolders } from '@/services/api';
import { toast } from 'react-hot-toast';
import './EmailCategory.css';

interface EmailCategoryProps {
  currentPage?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
  totalEmails?: number;
  emailsPerPage?: number;
  onPageChange?: (page: number) => void;
}

const folderIcons: Record<string, React.ReactNode> = {
  INBOX: <Mail size={20} />,
  Inbox: <Mail size={20} />,
  Starred: <Star size={20} />,
  Sent: <Send size={20} />,
  Drafts: <FileText size={20} />,
  Trash: <Trash2 size={20} />,
  Spam: <AlertCircle size={20} />,
  'Sent Messages': <Send size={20} />,
  'Draft Messages': <FileText size={20} />,
  Junk: <AlertCircle size={20} />,
  'All Mail': <Mail size={20} />,
  'All Folders': <Folder size={20} />,
};

function getFolderDisplayName(name: string, isCustom = false): string {
  if (!name) return 'Inbox';
  if (name === 'INBOX') return 'Inbox';
  if (isCustom) return name;
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

const EmailCategory = (props: EmailCategoryProps) => {
  const { folders, currentFolder, setCurrentFolder, fetchFolders } = useFolderStore();
  const { paginationState, handlePageChange } = usePagination();
  const navigate = useNavigate();
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFoldersDropdown, setShowFoldersDropdown] = useState(false);
  const [showAllFolders, setShowAllFolders] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        showFoldersDropdown &&
        !target.closest('.folders-dropdown') &&
        !target.closest('.gmail-folder-bar__mobile-trigger')
      ) {
        setShowFoldersDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showFoldersDropdown]);

  const currentPage = props.currentPage ?? paginationState.currentPage;
  const hasNext = props.hasNext ?? paginationState.hasNext;
  const hasPrev = props.hasPrev ?? paginationState.hasPrev;
  const totalEmails = props.totalEmails ?? paginationState.totalEmails;
  const emailsPerPage = props.emailsPerPage ?? paginationState.emailsPerPage;
  const onPageChange = props.onPageChange ?? handlePageChange;

  const systemFolders = folders.filter((folder) => folder.type === 'system');
  const customFolders = folders.filter((folder) => folder.type === 'custom');

  const activeFolderData = folders.find((f) => f.name === currentFolder);
  const isCustomFolder = activeFolderData?.type === 'custom';
  const displayName = getFolderDisplayName(currentFolder || 'INBOX', isCustomFolder);
  const folderIcon = folderIcons[currentFolder] ?? <Folder size={20} />;
  const rangeStart = totalEmails === 0 ? 0 : (currentPage - 1) * emailsPerPage + 1;
  const rangeEnd = Math.min(currentPage * emailsPerPage, totalEmails);
  const rangeLabel =
    totalEmails === 0 ? '0 of 0' : `${rangeStart}–${rangeEnd} of ${totalEmails.toLocaleString()}`;

  const handleFolderClick = (folderName: string) => {
    if (currentFolder === folderName) return;
    navigate(`/folder/${folderName}?page=1`);
    setCurrentFolder(folderName);
    setShowFoldersDropdown(false);
    onPageChange?.(1);
  };

  const handlePrevPage = () => {
    if (hasPrev && onPageChange) onPageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (hasNext && onPageChange) onPageChange(currentPage + 1);
  };

  const handleAddFolderClick = () => {
    setIsAddFolderModalOpen(true);
    setShowFoldersDropdown(false);
  };

  const handleSync = async () => {
    if (isSyncing) return;
    try {
      setIsSyncing(true);
      await syncFolders();
      await fetchFolders();
      toast.success('Folders synced successfully');
    } catch (error) {
      console.error('Failed to sync folders:', error);
      toast.error('Failed to sync folders. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const renderDropdownItem = (
    folderName: string,
    label: string,
    icon: React.ReactNode,
    unseen: number
  ) => (
    <button
      key={folderName}
      type="button"
      className={`folders-dropdown__item${currentFolder === folderName ? ' folders-dropdown__item--active' : ''}`}
      onClick={() => handleFolderClick(folderName)}
    >
      <span className="gmail-folder-bar__icon">{icon}</span>
      <span className="folders-dropdown__item-label">{label}</span>
      {unseen > 0 && (
        <span className="folders-dropdown__count">{formatBadgeCount(unseen)}</span>
      )}
    </button>
  );

  const renderFolderDropdown = () => (
    <div className="folders-dropdown" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="folders-dropdown__add" onClick={handleAddFolderClick}>
        <Plus size={18} />
        Create new folder
      </button>

      <div className="folders-dropdown__scroll">
        {systemFolders.slice(0, showAllFolders ? undefined : 5).map((folder) =>
          renderDropdownItem(
            folder.name,
            getFolderDisplayName(folder.name),
            folderIcons[folder.name] ?? <Folder size={18} />,
            folder.unseen
          )
        )}

        {systemFolders.length > 5 && !showAllFolders && (
          <button type="button" className="folders-dropdown__more" onClick={() => setShowAllFolders(true)}>
            {systemFolders.length - 5} more
          </button>
        )}

        {systemFolders.length > 0 && customFolders.length > 0 && (
          <div className="folders-dropdown__divider" />
        )}

        {customFolders.slice(0, showAllFolders ? undefined : 5).map((folder) =>
          renderDropdownItem(
            folder.name,
            folder.name,
            <Folder size={18} />,
            folder.unseen
          )
        )}

        {customFolders.length > 5 && !showAllFolders && (
          <button type="button" className="folders-dropdown__more" onClick={() => setShowAllFolders(true)}>
            {customFolders.length - 5} more
          </button>
        )}

        {showAllFolders && (systemFolders.length > 5 || customFolders.length > 5) && (
          <button type="button" className="folders-dropdown__more" onClick={() => setShowAllFolders(false)}>
            Show less
          </button>
        )}

        {folders.length === 0 && (
          <div className="folders-dropdown__empty">No folders available</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="gmail-folder-bar-wrapper">
        <div className="gmail-folder-bar">
          <div className="gmail-folder-bar__left">
            {isMobile ? (
              <>
                <button
                  type="button"
                  className="gmail-folder-bar__mobile-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFoldersDropdown(!showFoldersDropdown);
                  }}
                >
                  <span className="gmail-folder-bar__icon">{folderIcon}</span>
                  <h1 className="gmail-folder-bar__title">{displayName}</h1>
                  <ChevronDown
                    size={18}
                    className={`gmail-folder-bar__chevron${showFoldersDropdown ? ' gmail-folder-bar__chevron--open' : ''}`}
                  />
                </button>
                {showFoldersDropdown && renderFolderDropdown()}
              </>
            ) : (
              <>
                <span className="gmail-folder-bar__icon">{folderIcon}</span>
                <h1 className="gmail-folder-bar__title">{displayName}</h1>
              </>
            )}

            <div className="gmail-folder-bar__actions">
              <button
                type="button"
                className="gmail-folder-bar__icon-btn"
                onClick={handleAddFolderClick}
                title="Create new folder"
              >
                <Plus size={20} />
              </button>
              <button
                type="button"
                className={`gmail-folder-bar__icon-btn${isSyncing ? ' gmail-folder-bar__icon-btn--spin' : ''}`}
                onClick={handleSync}
                disabled={isSyncing}
                title="Sync folders"
              >
                <RotateCw size={18} />
              </button>
            </div>
          </div>

          <div className="gmail-folder-bar__pagination">
            <span className="gmail-folder-bar__range">{rangeLabel}</span>
            <button
              type="button"
              className="gmail-folder-bar__page-btn"
              onClick={handlePrevPage}
              disabled={!hasPrev}
              title="Newer"
              aria-label="Previous page"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="gmail-folder-bar__page-btn"
              onClick={handleNextPage}
              disabled={!hasNext}
              title="Older"
              aria-label="Next page"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <AddFolderModal
        isOpen={isAddFolderModalOpen}
        onClose={() => setIsAddFolderModalOpen(false)}
        onFolderAdded={() => fetchFolders()}
      />
    </>
  );
};

export default EmailCategory;
